import { ArrowRightIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { api } from '@/services/api.service'

// One family whose newly generated roadmap is held for provider review before
// the family sees it. Backend: GET /provider/roadmap-reviews.
interface ReviewItem {
    licenseId: string | null
    childName: string | null
    reviewBatchId: string | null
    generatedAt: string | null
    revisionCount: number
}

function formatDate(value?: string | null) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Provider-facing queue of roadmaps awaiting review. Every staff role can see it;
// the per-family page enforces who may edit/approve.
export default function ProviderRoadmapReviews() {
    const [items, setItems] = useState<ReviewItem[] | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        api<{ items: ReviewItem[] }>(ROUTES.provider.roadmapReviews).then((res) => {
            if (cancelled) return
            setItems(res.data?.items ?? [])
            setLoading(false)
        })
        return () => {
            cancelled = true
        }
    }, [])

    return (
        <div className='max-w-4xl mx-auto px-4 sm:px-6 py-8'>
            <div className='mb-6'>
                <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>Roadmap Reviews</h1>
                <p className='text-sm text-neutral-600 mt-1'>
                    Personalized roadmaps waiting for your review. Families see each one once you approve it.
                </p>
            </div>

            {loading ? (
                <div className='py-12 text-center text-sm text-neutral-500'>Loading…</div>
            ) : !items || items.length === 0 ? (
                <div className='bg-white border border-neutral-200 rounded-2xl p-10 text-center'>
                    <div className='w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3'>
                        <ClipboardDocumentCheckIcon className='w-6 h-6 text-neutral-400' />
                    </div>
                    <p className='text-sm font-medium text-neutral-700'>Nothing to review right now</p>
                    <p className='text-sm text-neutral-500 mt-1'>
                        When a family&apos;s roadmap is ready for your review, it will appear here.
                    </p>
                </div>
            ) : (
                <ul className='space-y-3'>
                    {items.map((item) => (
                        <li key={item.reviewBatchId ?? item.licenseId ?? Math.random()}>
                            <Link
                                to={item.licenseId ? `/clients/${item.licenseId}/roadmap` : '#'}
                                className='flex items-center gap-4 bg-white border border-neutral-200 rounded-xl p-4 hover:border-brand-300 hover:bg-brand-50/30 transition-colors'
                            >
                                <div className='w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0'>
                                    <ClipboardDocumentCheckIcon className='w-5 h-5 text-amber-600' />
                                </div>
                                <div className='flex-1 min-w-0'>
                                    <div className='text-sm font-medium text-neutral-900'>
                                        {item.childName ?? 'Client'}
                                    </div>
                                    <div className='flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-neutral-500 mt-0.5'>
                                        {item.generatedAt && <span>Prepared {formatDate(item.generatedAt)}</span>}
                                        {item.revisionCount > 0 && (
                                            <>
                                                <span>·</span>
                                                <span>
                                                    {item.revisionCount} revision{item.revisionCount === 1 ? '' : 's'}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <span className='inline-flex items-center gap-1 text-sm font-medium text-brand-600 flex-shrink-0'>
                                    Review
                                    <ArrowRightIcon className='w-4 h-4' />
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
